import React, { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  type MRT_Row,
  type MRT_TableOptions,
  type MRT_ColumnFiltersState,
  type MRT_PaginationState,
  type MRT_SortingState,
  useMaterialReactTable,
} from "material-react-table";

import {
  Box,
  Button,
  IconButton,
  Modal,
  Tooltip,
  Typography,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";

import {
  useGetFindings,
  useUpdateFinding,
  useDeleteFinding,
  useCreateFinding,
  usePollFindings,
  useExportFindings,
  useDeleteFindings,
} from "../../../hooks/findings";
import { Finding } from "../../../types/findings";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import ImportFromCSV from "../../csvimport/importFromCsv";
import { baseURL } from "../../../api/apiClient";

const FindingTable = () => {
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | undefined>
  >({});
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [globalFilter, setGlobalFilter] = useState<string | undefined>("");
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const openImportModal = () => setIsImportModalOpen(true);
  const closeImportModal = () => setIsImportModalOpen(false);

  useEffect(() => {
    setPagination({
      pageIndex: 0,
      pageSize: 10,
    });
  }, [columnFilters]);

  const { mutateAsync: deleteFinding } = useDeleteFinding();

  const {
    data: findingQueryResults = [],
    isError: isLoadingFindingsError,
    isFetching: isFetchingFindings,
    isLoading: isLoadingFindings,
  } = useGetFindings({ pagination, columnFilters, sorting, globalFilter });

  const fetchedFindings = findingQueryResults[0]?.findings || [];

  useEffect(() => {
    const count = findingQueryResults[0]?.rowCount;
    if (count == 0) {
      setRowCount(0);
    }
    if (!count || count === rowCount) {
      return;
    }
    setRowCount(count);
  }, [findingQueryResults]);

  const deleteFindings = useDeleteFindings(columnFilters, sorting, rowCount);

  const triggerDelete = async () => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete all currently filtered Findings?"
    );
    if (confirmDelete) {
      try {
        await deleteFindings();
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleSaveFinding: MRT_TableOptions<Finding>["onEditingRowSave"] =
    async ({ values, table }) => {
      const newValidationErrors = validateFinding(values);
      if (Object.values(newValidationErrors).some((error) => error)) {
        setValidationErrors(newValidationErrors);
        return;
      }
      setValidationErrors({});
      await updateFinding(values);
    };

  const handleCreateFinding: MRT_TableOptions<Finding>["onCreatingRowSave"] =
    async ({ values, table }) => {
      const newValidationErrors = validateFinding(values);
      if (Object.values(newValidationErrors).some((error) => error)) {
        setValidationErrors(newValidationErrors);
        return;
      }
      setValidationErrors({});
      await createFinding(values);
    };

  const openDeleteConfirmModal = (row: MRT_Row<Finding>) => {
    if (window.confirm("Are you sure you want to delete this Finding?")) {
      deleteFinding(row.original.id);
    }
  };

  const exportFindings = useExportFindings(columnFilters, sorting);

  const handleExport = async () => {
    try {
      await exportFindings();
    } catch (error) {
      toast.error("Failed to export");
    } finally {
    }
  };

  function validateFinding(finding: Finding) {
    const validateRequired = (value: any) => !!value;

    return {
      name: !validateRequired(finding.name) ? "Name is required" : "",
    };
  }

  const tableName = "findings_table";
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const tableState = JSON.parse(localStorage.getItem("tableState") || "{}");
    return (
      tableState[tableName]?.columnVisibility || {
        id: false,
      }
    );
  });

  useEffect(() => {
    localStorage.setItem(
      "tableState",
      JSON.stringify({
        ...JSON.parse(localStorage.getItem("tableState") || "{}"),
        [tableName]: {
          columnVisibility,
        },
      })
    );
  }, [columnVisibility, tableName]);

  const columns = useMemo<MRT_ColumnDef<Finding>[]>(
    () => [
      {
        accessorKey: "id",
        header: "Id",
        enableEditing: false,
        enableSorting: false,
        size: 80,
      },

      {
        accessorKey: "name",
        header: "Name",
        size: 220,
        enableEditing: (row) => !row.original.id,
        Cell: ({ row }) => {
          return (
            <>
              <Link to={`/finding/${row.original.event_id}`}>
                {row.original.name}
              </Link>
            </>
          );
        },
        muiEditTextFieldProps: {
          type: "text",
          required: true,
          error: !!validationErrors?.name,
          helperText: validationErrors?.name,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              name: undefined,
            }),
        },
      },

      {
        accessorKey: "event_time",
        header: "Time",
        size: 140,
        enableColumnFilter: true,
        enableSorting: true,
        muiEditTextFieldProps: {
          type: "text",
        },
      },
      {
        accessorKey: "notes",
        header: "Notes",
        size: 320,
        enableSorting: false,
        enableColumnFilter: false,
        muiEditTextFieldProps: {
          type: "text",
        },
      },
      {
        accessorKey: "details",
        header: "Details",
        size: 820,
        enableColumnFilter: true,
        //                Cell: ({ row }) => (
        //                    <div style={{ textAlign: "left", maxHeight: "310px" }}>
        //                        <ReactMarkdown>{row.original.details}</ReactMarkdown>
        //                    </div>
        //                ),
        Cell: ({ row }) => (
          <div
            style={{
              textAlign: "left",
              maxHeight: "5em",
            }}
          >
            <ReactMarkdown>{row.original.details}</ReactMarkdown>
          </div>
        ),

        enableSorting: false,
        enableEditing: false,
        muiEditTextFieldProps: {
          type: "text",
        },
      },
      {
        accessorKey: "assignee",
        header: "Analyst",
        size: 140,
        enableSorting: true,
        enableEditing: false,
        enableColumnFilter: true,
      },
      {
        accessorKey: "risk",
        header: "Risk",
        size: 40,
        enableSorting: true,
        enableEditing: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 60,
        enableSorting: true,
        enableEditing: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: "hostname",
        header: "Hostname",
        size: 140,
        enableSorting: true,
        enableEditing: false,
        enableColumnFilter: true,
      },
      {
        accessorKey: "event_id",
        header: "EventId",
        size: 30,
        enableSorting: true,
        enableEditing: false,
        enableColumnFilter: true,
      },
    ],
    [validationErrors]
  );
  const table = useMaterialReactTable({
    columns,
    enableGlobalFilter: false,
    data: fetchedFindings,
    initialState: {
      showColumnFilters: true,
      density: "compact",
      columnVisibility: columnVisibility,
    },
    onColumnVisibilityChange: setColumnVisibility,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    createDisplayMode: "modal",
    editDisplayMode: "modal",
    selectAllMode: "all",
    enableColumnResizing: true,
    getRowId: (row) => row.id,
    rowNumberDisplayMode: "original",
    muiToolbarAlertBannerProps: isLoadingFindingsError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    rowCount,
    muiTableContainerProps: {
      sx: {
        minHeight: "500px",
      },
    },
    renderBottomToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "1rem",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="contained"
          onClick={() => {
            table.setCreatingRow(true);
          }}
        >
          New Finding
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            usePollFindings();
          }}
        >
          Poll System
        </Button>
        <Button variant="contained" onClick={handleExport}>
          Export {rowCount} Rows
        </Button>
        <Button variant="contained" onClick={openImportModal}>
          Import
        </Button>
        <Modal
          open={isImportModalOpen}
          onClose={closeImportModal}
          id="modal"
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              height: "90vh",
              width: "90vw",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: "8px",
              overflow: "auto",
            }}
          >
            <Typography id="modal-title" variant="h6" component="h2">
              CSV Import
            </Typography>
            <hr />
            <Typography id="modal-description" sx={{ mt: 2 }}></Typography>
            <ImportFromCSV
              apiUrl={`${baseURL}/api/findings/import/`}
              importType="alerts"
              onClose={closeImportModal}
            />

            <Button onClick={closeImportModal} sx={{ mt: 2 }}>
              Close
            </Button>
          </Box>
        </Modal>
        <Button variant="contained" color="error" onClick={triggerDelete}>
          Delete {rowCount} Rows
        </Button>
      </Box>
    ),
    onCreatingRowCancel: () => setValidationErrors({}),
    onEditingRowCancel: () => setValidationErrors({}),
    onCreatingRowSave: handleCreateFinding,
    onEditingRowSave: handleSaveFinding,

    enableRowActions: true,
    renderRowActionMenuItems: ({ row, table }) => [
      <Box key={row.id}>
        <Tooltip title="Hide">
          <IconButton
            onClick={() => {
              row.original.userhidden = 1;
              updateFinding(row.original);
            }}
          >
            <VisibilityOffIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Make visible to users">
          <IconButton
            onClick={() => {
              row.original.userhidden = 0;
              updateFinding(row.original);
            }}
          >
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Resolve">
          <IconButton
            onClick={() => {
              row.original.status = "Resolved";
              updateFinding(row.original);
            }}
          >
            <ArrowDownwardIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Escalate">
          <IconButton
            onClick={() => {
              row.original.status = "Escalated";
              updateFinding(row.original);
            }}
          >
            <ArrowUpwardIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit">
          <IconButton onClick={() => table.setEditingRow(row)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton color="error" onClick={() => openDeleteConfirmModal(row)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>,
    ],
    state: {
      columnFilters,
      globalFilter,
      pagination,
      sorting,
      columnVisibility: columnVisibility,
      isLoading: isLoadingFindings,
      showAlertBanner: isLoadingFindingsError,
      showProgressBars: isFetchingFindings,
    },
  });

  const { mutateAsync: updateFinding } = useUpdateFinding({ table });
  const { mutateAsync: createFinding } = useCreateFinding({ table });

  return <MaterialReactTable table={table} />;
};
export default FindingTable;
